/**
 * TabSettingsModal - Modal for individual tab settings
 * Allows configuration of animations and color presets per tab
 */

import { devError } from '../../../utils/devLog';
import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Switch,
  Select,
  Button,
  Divider,
  Box,
  useToast
} from '@chakra-ui/react';
import { chartColorPresetManager } from '../../../config/chartColorPresets';

export interface TabSettings {
  enableAnimations: boolean;
  colorPreset: string;
}

export interface TabSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  chartId: string;
  currentSettings: TabSettings;
  onSave: (chartId: string, settings: TabSettings) => void;
}

/**
 * Available color presets
 */
const COLOR_PRESETS = [
  { id: 'default', name: 'Default (Clean)' },
  { id: 'bright-green', name: 'Bright Green' }
];

export const TabSettingsModal: React.FC<TabSettingsModalProps> = ({
  isOpen,
  onClose,
  chartId,
  currentSettings,
  onSave
}) => {
  const [settings, setSettings] = useState<TabSettings>(currentSettings);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // Reset settings when modal opens with new currentSettings
  useEffect(() => {
    setSettings(currentSettings);
  }, [currentSettings, isOpen]);

  const handleAnimationsChange = useCallback((checked: boolean) => {
    setSettings(prev => ({
      ...prev,
      enableAnimations: checked
    }));
  }, []);

  const handleColorPresetChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSettings(prev => ({
      ...prev,
      colorPreset: event.target.value
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      // Apply color preset immediately for preview
      chartColorPresetManager.applyPreset(settings.colorPreset);
      
      // Save settings
      onSave(chartId, settings);
      
      toast({
        title: 'Settings Saved',
        description: 'Tab settings have been updated successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
      
      onClose();
    } catch (error) {
      devError('Failed to save tab settings:', error);
      toast({
        title: 'Save Failed',
        description: 'Failed to save tab settings',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  }, [chartId, settings, onSave, onClose, toast]);

  const handleCancel = useCallback(() => {
    // Restore original settings
    setSettings(currentSettings);
    
    // Restore original color preset if it was changed
    if (settings.colorPreset !== currentSettings.colorPreset) {
      chartColorPresetManager.applyPreset(currentSettings.colorPreset);
    }
    
    onClose();
  }, [currentSettings, settings.colorPreset, onClose]);

  const hasChanges = 
    settings.enableAnimations !== currentSettings.enableAnimations ||
    settings.colorPreset !== currentSettings.colorPreset;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Tab Settings
          <Text fontSize="sm" color="gray.500" fontWeight="normal">
            Configure settings for this chart tab
          </Text>
        </ModalHeader>
        
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Animation Settings */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Animation Settings
              </Text>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm">Planet Animations</Text>
                  <Text fontSize="xs" color="gray.500">
                    Enable smooth animations for planet movements
                  </Text>
                </VStack>
                <Switch
                  isChecked={settings.enableAnimations}
                  onChange={(e) => handleAnimationsChange(e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </Box>

            <Divider />

            {/* Color Preset Settings */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Color Theme
              </Text>
              <VStack align="start" spacing={3}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm">Color Preset</Text>
                  <Text fontSize="xs" color="gray.500">
                    Choose a color scheme for the chart
                  </Text>
                </VStack>
                <Select
                  value={settings.colorPreset}
                  onChange={handleColorPresetChange}
                  size="sm"
                >
                  {COLOR_PRESETS.map(preset => (
                    <option key={preset.id} value={preset.id}>
                      {preset.name}
                    </option>
                  ))}
                </Select>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            variant="ghost" 
            onClick={handleCancel} 
            mr={3}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isLoading}
            isDisabled={!hasChanges}
          >
            Save Settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TabSettingsModal;